import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Wallet, MagnifyingGlass, PaperPlaneTilt, CaretLeft, CaretRight, Receipt, CaretUp, CaretDown } from '@phosphor-icons/react';
import BulkEmailClientsDialog from '@/components/BulkEmailClientsDialog';
import { PersonLink } from '@/components/PersonLink';

const EUR = (v) => (Number(v) || 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });

export default function RedeSaldosPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [diretosTotal, setDiretosTotal] = useState(0);
  const [somas, setSomas] = useState({ creditos: 0, comissao: 0, vales: 0 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [totalMin, setTotalMin] = useState('');
  const [totalMax, setTotalMax] = useState('');
  const [rangeMin, setRangeMin] = useState('');
  const [rangeMax, setRangeMax] = useState('');
  const [apenasDiretos, setApenasDiretos] = useState(false);
  const [componente, setComponente] = useState('');
  const [sortBy, setSortBy] = useState('total');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // ID -> {ID_Cliente,NomeCompleto,Email}
  const [showEmail, setShowEmail] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const filterPayload = useCallback(() => ({
    search: q, apenas_diretos: apenasDiretos, componente,
    total_min: rangeMin === '' ? null : Number(rangeMin),
    total_max: rangeMax === '' ? null : Number(rangeMax),
  }), [q, apenasDiretos, componente, rangeMin, rangeMax]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post('/rede/saldos', {
        ...filterPayload(), page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir,
      });
      const d = r.data;
      setRows(d.rows || []); setTotal(d.total || 0); setDiretosTotal(d.diretos_total || 0);
      setSomas(d.somas || { creditos: 0, comissao: 0, vales: 0 });
    } finally { setLoading(false); }
  }, [filterPayload, page, pageSize, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const toggle = (c) => setSelected((s) => {
    const n = { ...s };
    if (n[c.ID_Cliente]) delete n[c.ID_Cliente];
    else n[c.ID_Cliente] = { ID_Cliente: c.ID_Cliente, NomeCompleto: c.NomeCompleto, Email: c.Email };
    return n;
  });
  const allPageSelected = rows.length > 0 && rows.every((c) => selected[c.ID_Cliente]);
  const togglePage = () => setSelected((s) => {
    const n = { ...s };
    if (allPageSelected) rows.forEach((c) => delete n[c.ID_Cliente]);
    else rows.forEach((c) => { n[c.ID_Cliente] = { ID_Cliente: c.ID_Cliente, NomeCompleto: c.NomeCompleto, Email: c.Email }; });
    return n;
  });
  const selCount = Object.keys(selected).length;
  const doSearch = (e) => {
    e?.preventDefault();
    setPage(1);
    setQ(search.trim());
    setRangeMin(totalMin);
    setRangeMax(totalMax);
  };

  const sortOn = (col) => {
    setPage(1);
    if (sortBy === col) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  const applyPreset = (min, max) => {
    const sMin = min === null ? '' : String(min);
    const sMax = max === null ? '' : String(max);
    setTotalMin(sMin); setTotalMax(sMax);
    setRangeMin(sMin); setRangeMax(sMax);
    setPage(1);
  };

  const selectAllFiltered = async () => {
    setSelectingAll(true);
    try {
      const r = await api.post('/rede/saldos/ids', filterPayload());
      const list = r.data.clients || [];
      setSelected(() => {
        const n = {};
        list.forEach((c) => { n[c.ID_Cliente] = { ID_Cliente: c.ID_Cliente, NomeCompleto: c.NomeCompleto, Email: c.Email }; });
        return n;
      });
    } finally { setSelectingAll(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="rede-saldos-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-lg bg-[#0F1829] text-white grid place-items-center"><Wallet size={20} /></div>
        <div>
          <h1 className="text-2xl font-black tracking-tighter text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>Saldos da Rede</h1>
          <p className="text-sm text-[#6B7280]">Créditos, comissões e vales dos clientes da sua rede (exclui linhas a zero e consultores).</p>
        </div>
      </div>

      {/* Totais */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4">
        <Card label="Clientes" value={total} sub={`${diretosTotal} diretos`} />
        <Card label="Créditos" value={EUR(somas.creditos)} />
        <Card label="Comissões" value={EUR(somas.comissao)} />
        <Card label="Saldo de Vales" value={EUR(somas.vales)} />
      </div>

      {/* Filtros + ação */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <form onSubmit={doSearch} className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input data-testid="saldos-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome, email, telefone ou nº…"
              className="pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-md text-sm w-72 outline-none focus:border-[#0F1829]" />
          </div>
          <div className="flex items-center gap-1 text-sm text-[#6B7280]">
            <span className="text-xs">Total €</span>
            <input data-testid="saldos-total-min" type="number" step="0.01" value={totalMin} onChange={(e) => setTotalMin(e.target.value)}
              placeholder="mín" className="w-24 px-2 py-2 border border-[#E5E7EB] rounded-md text-sm outline-none focus:border-[#0F1829]" />
            <span>–</span>
            <input data-testid="saldos-total-max" type="number" step="0.01" value={totalMax} onChange={(e) => setTotalMax(e.target.value)}
              placeholder="máx" className="w-24 px-2 py-2 border border-[#E5E7EB] rounded-md text-sm outline-none focus:border-[#0F1829]" />
          </div>
          <button type="submit" className="px-3 py-2 text-sm bg-[#0F1829] text-white rounded-md">Pesquisar</button>
        </form>
        <label className="flex items-center gap-2 text-sm text-[#374151] cursor-pointer ml-1">
          <input type="checkbox" className="accent-[#0F1829]" checked={apenasDiretos} data-testid="saldos-apenas-diretos"
            onChange={(e) => { setApenasDiretos(e.target.checked); setPage(1); }} />
          Só diretos
        </label>
        <div className="flex-1" />
        <button disabled={total === 0 || selectingAll} onClick={selectAllFiltered} data-testid="saldos-select-all-filtered"
          className="px-3 py-2 text-sm font-medium border border-[#0F1829] text-[#0F1829] rounded-md hover:bg-[#F3F4F6] disabled:opacity-50">
          {selectingAll ? 'A selecionar…' : `Selecionar todos (${total})`}
        </button>
        {selCount > 0 && (
          <button onClick={() => setSelected({})} data-testid="saldos-clear-selection"
            className="px-3 py-2 text-sm text-[#6B7280] rounded-md hover:bg-[#F3F4F6]">Limpar</button>
        )}
        <button disabled={selCount === 0} onClick={() => setShowEmail(true)} data-testid="saldos-email-btn"
          className="px-4 py-2 text-sm font-medium bg-[#0F1829] text-white rounded-md hover:bg-[#002480] disabled:opacity-50 flex items-center gap-1.5">
          <PaperPlaneTilt size={15} /> Enviar email ({selCount})
        </button>
      </div>

      {/* Predefinições de intervalo */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
        <span className="text-[#9CA3AF] uppercase tracking-wider">Intervalos rápidos:</span>
        {[
          { label: '€0–50', min: 0, max: 50 },
          { label: '€50–100', min: 50, max: 100 },
          { label: '€100–500', min: 100, max: 500 },
          { label: 'Acima de €100', min: 100, max: null },
          { label: 'Acima de €500', min: 500, max: null },
        ].map((p) => {
          const active = rangeMin === String(p.min) && rangeMax === (p.max === null ? '' : String(p.max));
          return (
            <button key={p.label} onClick={() => applyPreset(p.min, p.max)} data-testid={`saldos-preset-${p.min}-${p.max ?? 'inf'}`}
              className={`px-2.5 py-1 rounded-full border ${active ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'border-[#E5E7EB] text-[#374151] hover:bg-[#F3F4F6]'}`}>
              {p.label}
            </button>
          );
        })}
        {(rangeMin !== '' || rangeMax !== '') && (
          <button onClick={() => applyPreset(null, null)} data-testid="saldos-preset-clear"
            className="px-2.5 py-1 rounded-full text-[#6B7280] hover:bg-[#F3F4F6]">Limpar intervalo</button>
        )}
        <span className="w-px h-4 bg-[#E5E7EB] mx-1" />
        <span className="text-[#9CA3AF] uppercase tracking-wider">Componente:</span>
        {[
          { label: 'Só créditos', val: 'creditos' },
          { label: 'Só comissões', val: 'comissao' },
          { label: 'Só vales', val: 'vales' },
        ].map((p) => {
          const active = componente === p.val;
          return (
            <button key={p.val} data-testid={`saldos-comp-${p.val}`}
              onClick={() => { setComponente(active ? '' : p.val); setPage(1); }}
              className={`px-2.5 py-1 rounded-full border ${active ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'border-[#E5E7EB] text-[#374151] hover:bg-[#F3F4F6]'}`}>
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Tabela */}
      <div className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="saldos-table">
          <thead className="bg-[#FAF8F2] text-left text-[11px] uppercase tracking-wider text-[#978D78]">
            <tr>
              <th className="px-3 py-2.5 w-10"><input type="checkbox" className="accent-[#0F1829]" checked={allPageSelected} onChange={togglePage} data-testid="saldos-select-page" /></th>
              <th className="px-3 py-2.5">Cliente</th>
              <th className="px-3 py-2.5">Tipo</th>
              <th className="px-3 py-2.5">Telefone</th>
              <th className="px-3 py-2.5">Email</th>
              <SortTh col="creditos" label="Créditos" sortBy={sortBy} sortDir={sortDir} onClick={sortOn} />
              <SortTh col="comissao" label="Comissões" sortBy={sortBy} sortDir={sortDir} onClick={sortOn} />
              <SortTh col="vales" label="Vales" sortBy={sortBy} sortDir={sortDir} onClick={sortOn} />
              <SortTh col="total" label="Total" sortBy={sortBy} sortDir={sortDir} onClick={sortOn} />
              <th className="px-3 py-2.5 text-center">Vales</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {loading ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-[#9CA3AF]">A carregar…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-[#9CA3AF]">Sem resultados.</td></tr>
            ) : rows.map((c) => (
              <tr key={c.ID_Cliente} className={selected[c.ID_Cliente] ? 'bg-[#EEF2FF]' : 'hover:bg-[#FAFAFA]'} data-testid={`saldos-row-${c.ID_Cliente}`}>
                <td className="px-3 py-2"><input type="checkbox" className="accent-[#0F1829]" checked={!!selected[c.ID_Cliente]} onChange={() => toggle(c)} data-testid={`saldos-check-${c.ID_Cliente}`} /></td>
                <td className="px-3 py-2 font-medium text-[#0F1829]">
                  <PersonLink id={c.ID_Cliente} name={c.NomeCompleto} className="font-medium" />
                  <span className="ml-2 text-[11px] font-mono text-[#9CA3AF]">#{c.ID_Cliente}</span>
                  {c.Direto && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold align-middle">DIRETO</span>}
                </td>
                <td className="px-3 py-2 text-[#6B7280]">{c.ID_TipoCliente}</td>
                <td className="px-3 py-2 font-mono text-[#6B7280]">{c.Telemovel || '—'}</td>
                <td className="px-3 py-2 text-[#6B7280]">{c.Email || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{EUR(c.Creditos)}</td>
                <td className="px-3 py-2 text-right font-mono">{EUR(c.Comissao)}</td>
                <td className="px-3 py-2 text-right font-mono">{EUR(c.SaldoVales)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-[#0F1829]">{EUR(c.Total)}</td>
                <td className="px-3 py-2 text-center">
                  <Link to={`/cliente/${c.ID_Cliente}/vales`} title="Ver vales" data-testid={`saldos-vales-${c.ID_Cliente}`}
                    className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#F3F4F6] text-[#0F1829]"><Receipt size={16} /></Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      <div className="flex items-center justify-between mt-3 text-sm text-[#6B7280]">
        <span>Página {page} de {totalPages} · {total} cliente(s)</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 border border-[#E5E7EB] rounded disabled:opacity-40"><CaretLeft size={16} /></button>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 border border-[#E5E7EB] rounded disabled:opacity-40"><CaretRight size={16} /></button>
        </div>
      </div>

      {showEmail && (
        <BulkEmailClientsDialog
          selectedClients={Object.values(selected)}
          onClose={() => setShowEmail(false)}
          onSent={() => { /* mantém seleção; utilizador fecha */ }}
        />
      )}
    </div>
  );
}

function Card({ label, value, sub }) {
  return (
    <div className="border border-[#E5E7EB] rounded-lg bg-white px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-medium">{label}</p>
      <p className="text-lg font-bold text-[#0F1829]">{value}</p>
      {sub && <p className="text-[11px] text-[#9CA3AF]">{sub}</p>}
    </div>
  );
}

function SortTh({ col, label, sortBy, sortDir, onClick }) {
  const active = sortBy === col;
  return (
    <th className="px-3 py-2.5 text-right">
      <button onClick={() => onClick(col)} data-testid={`saldos-sort-${col}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider ${active ? 'text-[#0F1829]' : 'hover:text-[#0F1829]'}`}>
        {label}
        {active ? (sortDir === 'desc' ? <CaretDown size={12} weight="bold" /> : <CaretUp size={12} weight="bold" />) : <CaretDown size={12} className="opacity-25" />}
      </button>
    </th>
  );
}

